<div
  class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-full tw-px-[30px]"
>
  @if (idleCountdown(); as countdown) {
    <div class="alert alert-warning idle-session-alert tw-w-full tw-mb-5">
      <!-- Announced once; the countdown is a timer so it isn't read out every second. -->
      <p role="alert" class="tw-mb-2.5">
        <strong>Are you still there?</strong>
        @if (screenSharingService.isModerated) {
          No one is sharing, so the session will end for all participants.
        } @else {
          You aren't sharing, so you'll be removed from the session.
        }
      </p>
      <p class="tw-mb-2.5">
        Time left:
        <strong role="timer">{{ countdown }}</strong>
      </p>
      <button rv-button class="tw-w-full" (click)="stayInSession()">
        Stay in Session
      </button>
    </div>
  }
  @if (waiting) {
    <img
      src="/images/screen-sharing/screen-sharing-wait.png"
      width="60"
      height="60"
      class="tw-mb-5"
      alt="Waiting"
    />
    <div class="tw-font-bold tw-text-[12.5px] tw-py-[12.5px]">
      Wait for the moderator to invite you to share
    </div>
  } @else if (screenSharingService.memberIsSharing) {
    @if (isPaused) {
      <img
        src="/images/screen-sharing/screen-sharing-paused.png"
        width="60"
        height="60"
        class="tw-mb-5"
        alt="Paused"
      />
      <strong
        class="tw-text-[18.75px] tw-font-semibold tw-mb-[15px] tw-text-center"
        >{{ isModerator ? 'Sharing paused' : 'Paused' }}</strong
      >
    } @else if (isStopped) {
      <img
        src="/images/screen-sharing/screen-sharing-stopped.png"
        width="60"
        height="60"
        class="tw-mb-5"
        alt="Stopped"
      />
      <strong
        class="tw-text-[18.75px] tw-font-semibold tw-mb-[15px] tw-text-center"
        >{{ isModerator ? 'Sharing stopped' : 'Stopped' }}</strong
      >
    } @else {
      <img
        src="/images/screen-sharing/screen-sharing-active.png"
        width="60"
        height="60"
        class="tw-mb-5"
        alt="Sharing"
      />
      <strong
        class="tw-text-[18.75px] tw-font-semibold tw-mb-[15px] tw-text-center"
        >Sharing your screen</strong
      >
    }
    @if (isModerator) {
      <p class="tw-text-[15px] tw-text-gray-600 tw-mb-[15px] tw-text-center">
        Your screen is being shared to the display.
      </p>
    }
  } @else if (isModerator) {
    @if (screenSharingService.sharing) {
      <initials-badge
        [name]="screenSharingService.sharing.memberDisplayName"
        [size]="80"
        class="tw-mb-[15px]"
      ></initials-badge>
      <strong
        class="tw-text-[18.75px] tw-font-semibold tw-mb-[15px] tw-text-center"
      >
        {{ screenSharingService.sharing.memberDisplayName }} is sharing
      </strong>
      <p class="tw-text-[15px] tw-text-gray-600 tw-mb-[15px] tw-text-center">
        You may invite another participant to share in their place.
      </p>
    } @else {
      <img
        src="/images/screen-sharing/user-circle-gray.png"
        width="60"
        height="60"
        class="tw-mb-5"
        alt="No User Sharing"
      />
      <strong
        class="tw-text-[18.75px] tw-font-semibold tw-mb-[15px] tw-text-center"
        >No one is sharing</strong
      >
      <p class="tw-text-[15px] tw-text-gray-600 tw-mb-[15px] tw-text-center">
        Select a participant to invite to share or share your screen.
      </p>
    }
  }

  @if (!isModerator) {
    @if (screenSharingService.channelError) {
      <div class="alert alert-danger">
        {{ screenSharingService.channelError }}
      </div>
    }
    <p class="tw-pb-[5px] tw-text-[11.25px]">
      @if (waiting) {
        <div class="moderator-list">
          To share your screen, you must be invited by
          <span [innerHTML]="moderatorName"></span>
        </div>
      } @else if (isPaused && screenSharingService.memberIsSharing) {
        Click "Resume" to resume screen sharing
      } @else if (isStopped) {
        Click "Start Sharing" to share your screen
      } @else {
        Your screen is being shared to the display
      }
    </p>
  }

  @if (!waiting) {
    <div class="tw-w-full tw-flex tw-flex-col tw-gap-2.5 tw-mt-[12.5px]">
      @if (isStopped) {
        <button
          rv-button
          (click)="start()"
          [disabled]="
            screenSharingService.connecting &&
            !rvshareAppDetectionService.isScreenShareApp()
          "
        >
          @if (
            screenSharingService.connecting &&
            !rvshareAppDetectionService.isScreenShareApp()
          ) {
            <mat-spinner diameter="15" class="white"></mat-spinner>
          } @else if (isModerator) {
            Share now
          } @else {
            Start Sharing
          }
        </button>
      } @else {
        @if (canResumeSharing) {
          <button rv-button class="tw-w-full" (click)="resume()">
            <mat-icon svgIcon="play-no-fill"></mat-icon>
            Resume Sharing
          </button>
        } @else if (screenSharingService.memberIsSharing) {
          <button rv-button variant="secondary" (click)="pause()">
            <mat-icon svgIcon="pause" aria-hidden="true"></mat-icon>
            Pause Sharing
          </button>
        }
        <button
          matButton="outlined"
          class="button-role--danger"
          (click)="stop()"
        >
          <mat-icon svgIcon="stop-circle" aria-hidden="true"></mat-icon>
          Stop Sharing
        </button>

        @if (isElectron) {
          <div class="tw-mt-[16px]">
            <mat-slide-toggle
              [ngModel]="screenSharingService.inputCaptureEnabled"
              (change)="toggleInputCapture($event.checked)"
            >
              Enable remote control
            </mat-slide-toggle>
          </div>
        }
      }
    </div>
    @if (sharerAudioWarning || showVolumeAndQuality) {
      <div class="tw-w-full tw-mt-[60px]">
        @if (sharerAudioWarning; as warning) {
          <div class="alert alert-warning sharer-audio-alert">
            {{ warning }}
          </div>
        }
        @if (showVolumeAndQuality) {
          @if (screenSharingService.muted) {
            <div class="alert alert-warning display-muted-alert">
              Audio is not supported on this display.
            </div>
          } @else {
            <div class="tw-mb-5">
              <strong class="tw-text-[15px] tw-font-semibold tw-block tw-mb-2.5"
                >Video Volume</strong
              >
              <div class="tw-flex tw-items-center tw-gap-2.5">
                <mat-icon
                  svgIcon="volume"
                  class="tw-text-gray-600 large"
                ></mat-icon>
                <mat-slider
                  min="0"
                  max="100"
                  step="1"
                  color="accent"
                  class="tw-flex-grow"
                  [disabled]="screenSharingService.muted"
                >
                  <input
                    matSliderThumb
                    [(value)]="screenSharingService.volume"
                    (valueChange)="changeVolume($event)"
                    aria-label="Volume control"
                  />
                </mat-slider>
                <div
                  class="volume tw-text-gray-900 tw-font-medium tw-text-[12.5px]"
                >
                  {{ screenSharingService.volume }}
                </div>
              </div>
            </div>
          }
          <div class="tw-mb-2.5">
            <strong class="tw-text-[15px] tw-font-semibold tw-block tw-mb-2.5"
              >Video Quality</strong
            >
            <mat-form-field
              appearance="outline"
              subscriptSizing="dynamic"
              class="tw-w-full"
            >
              <mat-select
                name="videoQuality"
                [(ngModel)]="screenSharingService.quality"
                (selectionChange)="onChangeQuality($event)"
              >
                <mat-option value="high">High Quality</mat-option>
                <mat-option value="medium">Medium Quality</mat-option>
                <mat-option value="low">Low Quality</mat-option>
              </mat-select>
            </mat-form-field>
          </div>
        }
      </div>
    }
  }
</div>
